import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHost,
  DialogPopup,
  DialogTitle,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTitleRow,
  Icon,
  Kbd,
  Sheet,
  SheetDescription,
  SheetFooter,
  SheetPopup,
  SheetTitle,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  usePresentationFlip,
  type Omittable,
  type ResizeSize,
} from "@salestrak/ui";
import {
  CONTENT_ANCHOR_ID,
  useAnchorInsets,
  type AnchorInsets,
} from "#app/lib/hooks/use-anchor-insets";
import {
  readSidebarWidth,
  sidebarWidthBounds,
  useReportDetailDrawerWidth,
  writeSidebarWidth,
} from "./detail-drawer-width";
import { DetailStackHost } from "./detail-stack";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

/** The stored desktop presentation. Mobile takes neither — it has one surface. */
export type DetailViewSize = "sidebar" | "fullscreen";

// The two desktop presentations are two different Base UI roots (a Sheet and a
// Dialog), so the a11y parts bound to each root differ. A header/footer shared
// across both takes them as input — same parts-record idiom as `IssueMenuParts`.
export interface DetailViewParts {
  Title: typeof DialogTitle | typeof SheetTitle;
  Description: typeof DialogDescription | typeof SheetDescription;
  Footer: typeof DialogFooter | typeof SheetFooter;
}
const DIALOG_PARTS: DetailViewParts = {
  Title: DialogTitle,
  Description: DialogDescription,
  Footer: DialogFooter,
};
const SHEET_PARTS: DetailViewParts = {
  Title: SheetTitle,
  Description: SheetDescription,
  Footer: SheetFooter,
};

/** The a11y parts bound to whichever root {@link DetailViewPopup} mounted. */
export function detailViewParts(drawer: boolean): DetailViewParts {
  return drawer ? SHEET_PARTS : DIALOG_PARTS;
}

// Fullscreen keeps a 4rem inset on every side and caps at 1600px.
export const FULLSCREEN_DIALOG_POPUP_CLASS =
  "h-[calc(100dvh-8rem)] w-[calc(100vw-8rem)] max-w-[1600px]";
const SIDEBAR_POPUP = "max-w-none";
// Anchored, the drawer's right edge rides the content card's rounded inner
// corners top and bottom — match them (card radius md minus its 1px border).
// The popup's floating `before:` top hairline comes off: it would sit flush
// on the card's border, and both border tokens are alpha, so the stacked
// edges paint brighter (dark) / darker (light) than the card's own border.
// The quiet-surface popup's edge shadow stays — it falls only off the OPEN
// (left) edge, interior to the card, so it never stacks on the card border.
const SIDEBAR_ANCHORED_CORNERS =
  "rounded-tr-[calc(var(--radius-md)-1px)] rounded-br-[calc(var(--radius-md)-1px)] before:hidden";

export type DetailViewPopupProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Anchored resizable side drawer (true) or fullscreen modal dialog. */
  drawer: boolean;
  /** aria-label on the drawer's drag seam — names the panel being resized. */
  resizeLabel: string;
  /** Fullscreen only: let the popup stick to the bottom edge on mobile. A
   *  nested dialog opts out so it doesn't fight its owner's presentation. */
  bottomStickOnMobile?: boolean;
  children: React.ReactNode;
}>;

/**
 * The presentation shell every large detail surface shares: a resizable side
 * drawer anchored inside the AppShell content card, or a fullscreen modal
 * dialog, with the switch between them morphed rather than popped.
 *
 * The drawer pins to the AppShell's content card (`CONTENT_ANCHOR_ID`) — the
 * region under the site header, inside the card's border — so it opens within
 * the card instead of spanning the viewport. Unlike the filter panel (which
 * pins to the below-tabs region), the card anchor sits ABOVE each route's view
 * tabs: a detail surface spans views conceptually; a filter belongs to one.
 */
export function DetailViewPopup({
  open,
  onOpenChange,
  drawer,
  resizeLabel,
  bottomStickOnMobile,
  children,
}: DetailViewPopupProps) {
  // Fullscreen↔drawer continuity: the two presentations are different Base UI
  // roots, so the library FLIP hook morphs the outgoing popup's rect into the
  // incoming one instead of letting the switch pop.
  const popupRef = usePresentationFlip(drawer ? "sidebar" : "fullscreen");
  const insets = useAnchorInsets(CONTENT_ANCHOR_ID, open && drawer);

  // The drawer is a non-modal layer: `modal={false}` plus
  // `disablePointerDismissal` leave the page behind it scrollable and
  // interactive, and `showBackdrop={false}` on the popup keeps it undimmed.
  if (drawer) {
    return (
      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        modal={false}
        disablePointerDismissal
      >
        <ResizableSidebarPopup
          insets={insets}
          open={open}
          popupRef={popupRef}
          resizeLabel={resizeLabel}
        >
          <DetailStackHost>{children}</DetailStackHost>
        </ResizableSidebarPopup>
      </Sheet>
    );
  }

  // Fullscreen is a modal layer with a backdrop, and clicking that backdrop
  // closes it — the dialog-system default (pointer dismissal stays on). It
  // paints the drawer's `background` fill, not the dialog default: the two
  // presentations are one page in two frames, and the scrim already isolates
  // the modal, so the fill does not have to.
  return (
    // Not responsive: phones render the Drawer branch below.
    <Dialog responsive={false} open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        bottomStickOnMobile={bottomStickOnMobile ?? true}
        className={FULLSCREEN_DIALOG_POPUP_CLASS}
        ref={popupRef}
        showCloseButton={false}
        surface="background"
      >
        <DetailStackHost>{children}</DetailStackHost>
      </DialogPopup>
    </Dialog>
  );
}

/**
 * The phone presentation of a create detail surface: a bottom drawer whose
 * header seats an outline × and Create as real buttons flanking the title — the
 * project create sheet's grammar. The panel is the `workspace` variant
 * (zero padding, no ScrollArea), so the same body a desktop popup hosts
 * keeps owning its full-bleed banner and its own scroller here.
 */
export function DetailViewCreateSheet({
  open,
  onOpenChange,
  onOpenChangeComplete,
  title,
  onCancel,
  onCreate,
  children,
}: Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The settle signal: fires once the open animation lands. Gate any focus
   *  claim on it — focusing mid-snap opens the virtual keyboard, which
   *  resizes the visual viewport and desyncs the drawer's transform math. */
  onOpenChangeComplete?: (open: boolean) => void;
  title: string;
  onCancel: () => void;
  onCreate: () => void;
  children: React.ReactNode;
}>) {
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      // A detail surface is worked in: the top stop alone, like the project
      // sheets (issue-view-sheet.tsx).
      size="full"
    >
      <DrawerPopup showBar surface="background">
        <DrawerHeader>
          <DrawerTitleRow
            dismiss={{ onClick: onCancel }}
            primary={{ label: "Create", onClick: onCreate }}
          >
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerTitleRow>
        </DrawerHeader>
        <DrawerPanel variant="workspace">
          <DetailStackHost>{children}</DetailStackHost>
        </DrawerPanel>
        {/* App-level dialogs opened from this sheet stack on it. */}
        <DialogHost />
      </DrawerPopup>
    </Drawer>
  );
}

/**
 * The phone presentation of a settled detail surface: a bottom drawer whose
 * title row carries the actions cluster on its trailing edge — the project
 * view sheet's grammar. The `workspace` panel keeps the tab strip fixed under
 * the title while each TabsPanel scrolls, exactly as the desktop dialog lays
 * out; `footer` takes a DrawerFooter pinned below the panel. The popup is
 * `fill`: content-sized, the sheet would shrink to whichever tab is active —
 * an empty Contacts tab collapsed the whole surface — so it holds the full
 * drawer height and only the snap points move it.
 */
export function DetailViewSheet({
  open,
  onOpenChange,
  title,
  actions,
  footer,
  children,
}: Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The title row's trailing cluster — typically the "…" menu. */
  actions?: React.ReactNode;
  /** A DrawerFooter, when something must stay pinned below the panel. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}>) {
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="full"
    >
      <DrawerPopup showBar surface="background">
        <DrawerHeader>
          <Box direction="row" align="center" justify="between" gap="sm">
            {/* The title takes the row so its truncation is measured against
                the whole width it could have, never against the cluster. */}
            <Box direction="row" align="center" gap="sm" fill="flex" minWidth={0}>
              <DrawerTitle>{title}</DrawerTitle>
            </Box>
            <Box direction="row" align="center" gap="xs" shrink={false}>
              {actions}
            </Box>
          </Box>
        </DrawerHeader>
        <DrawerPanel variant="workspace">
          <DetailStackHost>{children}</DetailStackHost>
        </DrawerPanel>
        {footer}
        {/* App-level dialogs opened from this sheet stack on it. */}
        <DialogHost />
      </DrawerPopup>
    </Drawer>
  );
}

/** The flush header row every detail view opens with: breadcrumb on the left,
 *  action cluster on the right, both in flow rather than absolutely positioned
 *  in the corner. */
export function DetailViewHeaderRow({
  parts,
  title,
  description,
  breadcrumb,
  actions,
}: {
  parts: DetailViewParts;
  title: string;
  /** Read after the title by a screen reader, so only a sentence that adds
   *  to it belongs here — never an id. Omit it when there is none. */
  description?: string;
  breadcrumb: React.ReactNode;
  actions: React.ReactNode;
}) {
  const { Title, Description } = parts;
  return (
    // First line pinned to the shell-wide 28px contract (see PageHeader) so
    // the row holds its height — and the tab separator below holds the
    // workflow route's 80px line — whatever mix of 24px crumbs and 28px
    // actions it carries.
    <Box
      direction="row"
      align="center"
      gap="sm"
      minHeight={44}
      paddingX="md"
      paddingY="sm"
      shrink={false}
    >
      <Box shrink fill="flex">
        <Title visuallyHidden>{title}</Title>
        {description ? <Description visuallyHidden>{description}</Description> : null}
        {breadcrumb}
      </Box>
      <Box direction="row" align="center" gap="2xs" shrink={false}>
        {actions}
      </Box>
    </Box>
  );
}

/** The drawer↔fullscreen flip is an ordinary header button. */
export function DetailViewSizeToggle({
  desktopSize,
  setDesktopSize,
}: {
  desktopSize: DetailViewSize;
  setDesktopSize: (size: DetailViewSize) => void;
}) {
  const toFullscreen = desktopSize === "sidebar";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              const size = toFullscreen ? "fullscreen" : "sidebar";
              trackEvent({
                context: AnalyticsContext.Issue,
                event: IssueEvent.ChangeDetailSize,
                data: { size, via: "toggle" },
              });
              setDesktopSize(size);
            }}
            aria-label={
              toFullscreen ? "Expand to fullscreen" : "Shrink to sidebar"
            }
          />
        }
      >
        <Icon name={toFullscreen ? "maximize" : "minimize"} size="sm" />
      </TooltipTrigger>
      <TooltipPopup side="bottom">Switch layout</TooltipPopup>
    </Tooltip>
  );
}

/** The header's favorite star. Tooltips in this row are short verbs; the
 *  aria-label carries the noun so a screen reader hears what is starred. */
export function DetailViewFavoriteButton({
  favorited,
  onToggle,
  subject,
}: {
  favorited: boolean;
  onToggle: () => void;
  /** The entity noun — "project", "Account", "Contact". */
  subject: string;
}) {
  const verb = favorited ? "Unfavorite" : "Favorite";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={`${verb} ${subject}`}
            onClick={onToggle}
            size="icon-sm"
            variant="ghost"
          />
        }
      >
        <Icon
          className={favorited ? "text-warning-foreground" : undefined}
          color={favorited ? undefined : "muted"}
          name={favorited ? "star-filled" : "star"}
          size="sm"
        />
      </TooltipTrigger>
      <TooltipPopup side="bottom">{verb}</TooltipPopup>
    </Tooltip>
  );
}

export function DetailViewCloseButton({
  asPanel,
  panelLabel,
  onClose,
}: {
  asPanel: boolean;
  /** aria-label while the surface is a side panel — it closes a panel, not a
   *  modal, so the label names what leaves the screen. */
  panelLabel: string;
  onClose: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={asPanel ? panelLabel : "Close"}
            size="icon-sm"
            color="neutral"
            onClick={onClose}
          />
        }
      >
        <Icon name={asPanel ? "drawer-closed-right" : "x"} size="sm" />
      </TooltipTrigger>
      <TooltipPopup side="bottom">
        <Box align="center" direction="row" gap="xs">
          <Text size="xs">Close</Text>
          <Kbd>Esc</Kbd>
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}

/**
 * The right-side sheet with the drag seam on its inner edge. Streamed widths
 * write straight to the element (no state churn per pointermove, matching the
 * particle's contract); the final width persists once on release. The page
 * beneath learns the rendered width through `DetailDrawerWidthProvider` —
 * on open, every drag frame, and 0 on close.
 */
function ResizableSidebarPopup({
  insets,
  open,
  popupRef,
  resizeLabel,
  children,
}: {
  insets: AnchorInsets | null;
  open: boolean;
  /** Shared popup element ref — the fullscreen↔drawer FLIP measures it. */
  popupRef: React.RefObject<HTMLDivElement | null>;
  resizeLabel: string;
  children: React.ReactNode;
}) {
  // The viewport is the sheet's container, so its width fixes both the drag
  // bounds and the starting width for this open.
  const [{ bounds, width }] = useState(() =>
    typeof window === "undefined"
      ? { bounds: { max: 0, min: 0 }, width: 0 }
      : {
          bounds: sidebarWidthBounds(window.innerWidth),
          width: readSidebarWidth(window.innerWidth),
        },
  );
  const finalRef = useRef(width);
  const reportWidth = useReportDetailDrawerWidth();
  // Layout effect: the page's chrome moves out from under the drawer in the
  // same commit the drawer appears, not a paint later.
  useLayoutEffect(() => {
    reportWidth(open ? width : 0);
    return () => reportWidth(0);
  }, [open, reportWidth, width]);

  // Streamed widths go straight to the element: this popup wraps an entire
  // detail view, and a re-render per frame of the drag is not worth paying.
  // The page beneath does re-render per frame — its chrome has to keep clear
  // of the seam while it moves.
  const onResize = useCallback(
    ({ width: next }: ResizeSize) => {
      finalRef.current = next;
      popupRef.current?.style.setProperty("width", `${next}px`);
      reportWidth(next);
    },
    [popupRef, reportWidth],
  );

  const onResizeEnd = useCallback(() => {
    writeSidebarWidth(finalRef.current);
    trackEvent({
      context: AnalyticsContext.Issue,
      event: IssueEvent.ChangeDetailSize,
      // A 50px bucket: the width shape, not the exact pixel.
      data: { size: "sidebar", via: "resize", width: Math.round(finalRef.current / 50) * 50 },
    });
  }, []);

  return (
    <SheetPopup
      className={
        insets ? `${SIDEBAR_POPUP} ${SIDEBAR_ANCHORED_CORNERS}` : SIDEBAR_POPUP
      }
      ref={popupRef}
      // Opening must not focus into the drawer: the first tabbable is the
      // resize seam (the resize Box IS the popup root), and Base UI's
      // mount-focus lights its focus ring — a blue edge nobody asked for.
      // Focus rests on the popup; the user tabs in. Same call as the
      // AppShell's mobile nav drawer.
      initialFocus={false}
      style={
        insets
          ? {
              marginTop: insets.top,
              marginRight: insets.right,
              marginBottom: insets.bottom,
            }
          : undefined
      }
      render={
        <Box
          maxWidth={bounds.max || undefined}
          minWidth={bounds.min || undefined}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          resize="left"
          resizeLabel={resizeLabel}
          width={width || undefined}
        />
      }
      showBackdrop={false}
      showCloseButton={false}
      // Anchored inside the content card, the drawer is a panel of the page,
      // not a floating layer — it shares the page fill so the seam is the
      // only divider.
      surface="background"
    >
      {children}
    </SheetPopup>
  );
}
